import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { captureFirstTouch, getFirstTouch } from '../lib/attribution';

// Auth relies solely on the httpOnly `session_token` cookie set by the backend.
// Ensure every axios request (including plain `axios` calls across the app) sends cookies.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// Non-sensitive hint so we skip the /auth/me probe for visitors who never logged in
// (the real session is the backend httpOnly cookie; this is only a client-side optimization).
const SESSION_HINT = 'inasw_session';
const hasSessionHint = () => {
  try { return localStorage.getItem(SESSION_HINT) === '1'; } catch { return false; }
};
const setSessionHint = () => { try { localStorage.setItem(SESSION_HINT, '1'); } catch { /* ignore */ } };
const clearSessionHint = () => { try { localStorage.removeItem(SESSION_HINT); } catch { /* ignore */ } };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const axiosInstance = axios.create({
    baseURL: API_URL,
    withCredentials: true,
  });

  useEffect(() => {
    captureFirstTouch();
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    // Skip the probe (and its 401) for visitors who have never logged in on this device.
    if (!hasSessionHint()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axiosInstance.get('/auth/me');
      setUser(response.data);
      setSessionHint();
    } catch (error) {
      clearSessionHint();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    setUser(response.data.user);
    setSessionHint();
    return response.data.user;
  };

  // Login after obtaining a session via another flow (e.g. SW Express Apply).
  // The backend has already set the httpOnly cookie, so just refresh the user.
  const loginWithToken = async () => {
    setSessionHint();
    await checkAuth();
  };

  const register = async (email, password, name, userType, onboardingRole, attribution = {}) => {
    const ft = getFirstTouch();
    const response = await axiosInstance.post('/auth/register', {
      email,
      password,
      name,
      user_type: userType,
      onboarding_role: onboardingRole || undefined,
      marketing_source: attribution.marketing_source || undefined,
      marketing_source_detail: attribution.marketing_source_detail || undefined,
      utm_source: ft.utm_source || undefined,
      utm_medium: ft.utm_medium || undefined,
      utm_campaign: ft.utm_campaign || undefined,
      utm_term: ft.utm_term || undefined,
      utm_content: ft.utm_content || undefined,
      referrer: ft.referrer || undefined,
      landing_page: ft.landing_page || undefined,
      featured_referral_code: attribution.featured_referral_code || undefined,
    });
    setUser(response.data.user);
    setSessionHint();
    return response.data.user;
  };

  const googleLogin = async (sessionId) => {
    const response = await axiosInstance.post('/auth/google/session', { session_id: sessionId });
    setUser(response.data.user);
    setSessionHint();
    return response.data.user;
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearSessionHint();
    setUser(null);
  };

  const updateUserType = async (userType) => {
    const response = await axiosInstance.put('/auth/user-type', { user_type: userType });
    setUser(response.data);
    return response.data;
  };

  const updateProfile = async (profileData) => {
    const response = await axiosInstance.put('/users/profile', profileData);
    setUser(response.data);
    return response.data;
  };

  const uploadResume = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/users/resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    await checkAuth(); // Refresh user data
    return response.data;
  };

  const value = {
    user,
    loading,
    login,
    loginWithToken,
    register,
    googleLogin,
    logout,
    updateUserType,
    updateProfile,
    uploadResume,
    checkAuth,
    isAuthenticated: !!user,
    isJobSeeker: user?.user_type === 'job_seeker',
    isEmployer: user?.user_type === 'employer' || (user?.roles || []).includes('employer'),
    isSupervisor: user?.user_type === 'supervisor',
    isAdmin: user?.user_type === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
